import { isArray } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { QueryParamNames } from '~/constants/search';
import { setQueryParams } from '~/utils/PathUtils';

export type PersonKind = 'face' | 'clipPerson';

const PERSON_KIND_TO_PARAM: Record<PersonKind, QueryParamNames> = {
  face: QueryParamNames.faceId,
  clipPerson: QueryParamNames.clipPersonId,
};

export const clearAllSelectedPersonIds = () =>
  setQueryParams({
    [QueryParamNames.faceId]: null,
    [QueryParamNames.clipPersonId]: null,
  });

export const useSelectedPersonId = (kind: PersonKind) => {
  const { query } = useRouter();
  const ownKey = PERSON_KIND_TO_PARAM[kind];
  const otherKey = kind === 'face' ? QueryParamNames.clipPersonId : QueryParamNames.faceId;
  const selectedValue = query[ownKey];

  const isSelected = !!selectedValue;

  const selectedPersonId = useMemo(() => {
    if (isArray(selectedValue)) {
      return selectedValue[0];
    }

    return selectedValue;
  }, [selectedValue]);

  // Selecting either kind clears the other — they're mutually exclusive
  // "what's currently focused on the people panel".
  const selectPersonById = useCallback(
    (personId: string) => {
      return setQueryParams({
        [ownKey]: personId,
        [otherKey]: null,
        [QueryParamNames.discussionId]: null,
      });
    },
    [otherKey, ownKey],
  );

  const clearSelectedPersonId = useCallback(() => {
    if (isSelected) {
      return setQueryParams({ [ownKey]: null });
    }
  }, [isSelected, ownKey]);

  const toggleSelectedPerson = useCallback(
    (personId: string) => {
      if (selectedPersonId === personId) {
        clearSelectedPersonId();
      } else {
        selectPersonById(personId);
      }
    },
    [clearSelectedPersonId, selectPersonById, selectedPersonId],
  );

  return {
    selectedPersonId,
    selectPersonById,
    clearSelectedPersonId,
    toggleSelectedPerson,
  };
};
